import { ReactNode } from 'react';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import { getInitials } from 'utilities';

interface CellProps {
  cell?: unknown;
  disabled?: boolean;
}

export interface ModelCompanyNameCellProps extends CellProps {
  modelRow: ModelWithValuationData;
}

export const ModelCompanyNameCell = ({ modelRow }: ModelCompanyNameCellProps) => {
  let engagementSuffix: ReactNode = null;
  if (modelRow.is_loading_valuation_data) {
    engagementSuffix = (
      <Skeleton variant="text" width={80} sx={{ display: 'inline-block', ml: 0.5, verticalAlign: 'middle' }} />
    );
  } else if (modelRow.engagement_type) {
    engagementSuffix = ` ${modelRow.engagement_type}`;
  }

  return (
    <Typography
      variant="body2"
      sx={{ display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
      {modelRow.company_name}
      {engagementSuffix}
    </Typography>
  );
};

export interface ModelValuationLeadCellProps extends CellProps {
  modelRow: ModelWithValuationData;
}

export const ModelValuationLeadCell = ({ modelRow }: ModelValuationLeadCellProps) => {
  if (modelRow.is_loading_valuation_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width={120} height={20} />
      </Box>
    );
  }
  if (modelRow.valuation_lead && modelRow.valuation_lead !== '-') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          alt={modelRow.valuation_lead}
          src={modelRow.valuation_lead_image_src}
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: modelRow.valuation_lead })}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {modelRow.valuation_lead}
        </Typography>
      </Box>
    );
  }
  return <Typography variant="body2" color="text.secondary">-</Typography>;
};

export interface ModelValuationTextCellProps extends CellProps {
  modelRow: ModelWithValuationData;
  field: 'valuation_status' | 'valuation_date';
}

// Shows a skeleton while Attio enrichment is in flight so "-" is not mistaken for missing data.
export const ModelValuationTextCell = ({ modelRow, field }: ModelValuationTextCellProps) => {
  if (modelRow.is_loading_valuation_data) {
    return <Skeleton variant="text" width="80%" height={20} />;
  }

  const value = field === 'valuation_status' ? modelRow.valuation_status : modelRow.valuation_date;
  return <Typography variant="body2">{value || '-'}</Typography>;
};
